import React, { useCallback, useEffect, useRef, useState } from "react";

import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import Alert from "react-bootstrap/Alert";
import Form from "react-bootstrap/Form";
import { useSelector } from "react-redux";
import ClipLoader from "react-spinners/ClipLoader";

import "../css/createTikTokDestination.css";
import {
    getTiktokAuthUrl,
    getTiktokPages,
    tiktokAuthorize,
} from "../helpers/tiktok-lead/tiktok-lead";
import LoginButton from "./molecules/LoginButton";
import MuiSelect from "./molecules/MuiSelect";
import SwalAlertComp from "./SwalAlert";

const TiktokLeadLogin = (props) => {
    const [createStatus, setCreateStatus] = useState("idle");
    const [showComponent, setShowComponent] = useState(false);
    const [RedirectState, setRedirectState] = useState();
    const [authAdvertisers, setAdvertisers] = useState([]);
    const [authButtonVisible, setAuthButtonVisible] = useState(true);
    const [sourceName, setSourceName] = useState("");
    const [selectedAdvertiserId, setSelectedAdvertiserId] = useState("");
    const [selectedAdvertiserPageIds, setSelectedAdvertiserPageIds] = useState(
        [],
    );
    const [selectedAdvertiserPages, setSelectedAdvertiserPages] = useState([]);
    const [pagesAreaStatus, setPagesAreaStatus] = useState(false);
    const [pagesLoader, setPagesLoader] = useState(false);

    const isAuthorizing = useRef(false);
    const language = useSelector((state) => state.languagesState.languages);

    const getAuthUrl = useCallback(async () => {
        setCreateStatus(language?.lead?.loading ?? "Loading");
        const response = await getTiktokAuthUrl();
        if (response.status) {
            isAuthorizing.current = false;
            setPagesLoader(false);
            setShowComponent(true);
            return response?.data?.auth_url ?? null;
        } else {
            setCreateStatus("idle");
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message,
                icon: "error",
            });
            return null;
        }
    }, [language]);

    const authorize = useCallback(
        async ({ code, state }) => {
            if (isAuthorizing.current) return;
            isAuthorizing.current = true;
            setShowComponent(false);
            setCreateStatus(language?.lead?.loading ?? "Loading");
            const response = await tiktokAuthorize({ code, state });
            if (response.status) {
                if (props?.setTiktokAppStatus !== undefined) {
                    props?.setTiktokAppStatus("success");
                }
                setCreateStatus(
                    language?.delete_swal_modal?.success ?? "Success",
                );
                setAdvertisers(response?.data?.advertisers);
                setRedirectState(response?.data?.state);
                setAuthButtonVisible(false);
            } else {
                if (props?.setTiktokAppStatus !== undefined) {
                    props?.setTiktokAppStatus("error");
                }
                setCreateStatus("idle");
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message,
                    icon: "error",
                });
            }
        },
        [language, props],
    );

    const handleAdvertiserId = async (value) => {
        setSelectedAdvertiserId(value);
        setPagesAreaStatus(true);
        setPagesLoader(true);
        let response = await getTiktokPages(RedirectState, value);
        if (response.status) {
            setSelectedAdvertiserPages(response?.data?.pages);
            setPagesLoader(false);
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message:
                    language?.destination_modal?.tiktok_page_error ??
                    "The Page for the Advertiser you selected could not be found.",
                icon: "error",
            });
            setPagesAreaStatus(false);
            setPagesLoader(false);
        }
    };

    const handleAdvertiserPageIds = (value) => {
        setSelectedAdvertiserPageIds(
            typeof value === "string" ? value.split(",") : value,
        );
        props?.setTiktokForms(
            typeof value === "string" ? value.split(",") : value,
        );
    };

    useEffect(() => {
        if (props?.setStoreData !== undefined) {
            let obj = {
                state: RedirectState,
                advertiser: selectedAdvertiserId,
                selected_pages: selectedAdvertiserPageIds,
                name: sourceName,
            };
            props?.setStoreData(obj);
        }
    }, [
        sourceName,
        RedirectState,
        selectedAdvertiserId,
        selectedAdvertiserPageIds,
    ]);

    const handleReceiveNewForms = (event) => {
        if (props?.setRecieveNewForms !== undefined) {
            props?.setRecieveNewForms(event.target.checked);
        }
    };

    return (
        <div className="w-full">
            {authButtonVisible ? (
                <div className="position-relative overflow-hidden z-1">
                    <LoginButton
                        code="tiktok"
                        className="mb-3 tiktok-btn"
                        getAuthUrl={getAuthUrl}
                        paramNames={["code", "state"]}
                        requireAllParams
                        onSuccess={authorize}
                        onError={() => {
                            setCreateStatus("idle");
                            setShowComponent(false);
                        }}
                        onAuthUrlError={() => {
                            setCreateStatus("idle");
                            setShowComponent(false);
                        }}
                        logoSrc="/assets/images/typeImages/tiktok-icon.png"
                        buttonText={
                            showComponent
                                ? (language?.lead?.you_have_new_window_open ??
                                  "You have a new window open")
                                : (language?.destination_modal
                                      ?.continue_with_tiktok ??
                                  "Continue with Tiktok")
                        }
                        disabled={showComponent || createStatus === "Loading"}
                        isLoadingExternal={createStatus === "Loading"}
                    />
                </div>
            ) : (
                <div>
                    <div>
                        <FormControl className="mb-3" style={{ width: "100%" }}>
                            <TextField
                                name="name"
                                label={
                                    language?.destination_modal?.source_name ??
                                    "Source Name"
                                }
                                variant="outlined"
                                onChange={(e) => {
                                    setSourceName(e.target.value);
                                    props?.setSourceName(e.target.value);
                                }}
                                placeholder={
                                    language?.destination_modal?.source_name ??
                                    "Source Name"
                                }
                                value={sourceName}
                            />
                        </FormControl>
                    </div>
                    {authAdvertisers && authAdvertisers.length > 0 && (
                        <div className="mb-3">
                            <Form.Group controlId="exampleForm.ControlInput1">
                                <MuiSelect
                                    value={selectedAdvertiserId}
                                    options={
                                        authAdvertisers?.map((advertiser) => ({
                                            label: advertiser?.advertiser_name,
                                            value: advertiser?.advertiser_id,
                                        })) || []
                                    }
                                    setValue={handleAdvertiserId}
                                    label={
                                        language?.destination_modal
                                            ?.advertisers ?? "Advertisers"
                                    }
                                    labelId="tiktok-advertiser-select-label"
                                    className="tour-tiktok-advertiser-select"
                                    disableCloseOnSelect={false}
                                />
                            </Form.Group>
                        </div>
                    )}
                    {pagesAreaStatus && (
                        <div className="mb-3">
                            {pagesLoader ? (
                                <div className="sweet-loading">
                                    <ClipLoader color="#729abf" size={50} />
                                </div>
                            ) : selectedAdvertiserPages &&
                              selectedAdvertiserPages.length > 0 ? (
                                <Form.Group controlId="exampleForm.ControlInput1">
                                    <MuiSelect
                                        value={selectedAdvertiserPageIds}
                                        options={
                                            selectedAdvertiserPages?.map(
                                                (page) => ({
                                                    label: page?.page_name,
                                                    value: page?.page_id,
                                                }),
                                            ) || []
                                        }
                                        setValue={handleAdvertiserPageIds}
                                        label={
                                            language?.destination_modal
                                                ?.forms ?? "Forms"
                                        }
                                        labelId="facebook-page-select-label"
                                        className="tour-tiktok-form-select"
                                        multiple
                                        withChip
                                        renderSelectAll
                                    />
                                </Form.Group>
                            ) : (
                                <Alert key={"danger"} variant={"danger"}>
                                    {language?.destination_modal
                                        ?.tiktok_no_pages_message ??
                                        "No Instant form has been found for the advertiser you selected."}
                                </Alert>
                            )}
                        </div>
                    )}
                    {props?.receiveNewForms !== undefined && (
                        <div className="mb-3">
                            <FormControlLabel
                                className="newForm m-0 tour-titkok-receiver-forms-checkbox"
                                control={
                                    <Checkbox
                                        checked={props?.receiveNewForms}
                                        onChange={handleReceiveNewForms}
                                    />
                                }
                                label={
                                    language?.lead?.forms_automatically ??
                                    "Add new forms automatically"
                                }
                            />
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    pl: 5.5,
                                }}
                            >
                                {language?.lead?.new_created_forms ??
                                    "New created forms will be included in your integration automatically"}
                            </Typography>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TiktokLeadLogin;
